import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';

interface SidebarContextType {
  isSidebarOpen: boolean;
  setSidebarOpen: (isOpen: boolean) => void;
  toggleSidebar: () => void;
  isTopBarVisible: boolean;
  setTopBarVisible: (visible: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const ANCHO_MAXIMO_COLAPSO_AUTOMATICO = 1300;
const ANCHO_MAXIMO_MOVIL = 767;

const esPantallaMovil = () => window.innerWidth <= ANCHO_MAXIMO_MOVIL;

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth > ANCHO_MAXIMO_COLAPSO_AUTOMATICO);
  const [isTopBarVisible, setIsTopBarVisible] = useState(true);

  // Detectar ancho de ventana para colapsar sidebar automáticamente
  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth > ANCHO_MAXIMO_COLAPSO_AUTOMATICO);
    };

    // Ejecutar al montar
    handleResize();

    // Escuchar cambios de tamaño
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const setSidebarOpen = useCallback((isOpen: boolean) => {
    setIsSidebarOpen(isOpen && !esPantallaMovil());
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  const setTopBarVisible = useCallback((visible: boolean) => {
    setIsTopBarVisible(visible);
  }, []);

  return (
    <SidebarContext.Provider value={{ isSidebarOpen, setSidebarOpen, toggleSidebar, isTopBarVisible, setTopBarVisible }}>
      {children}
    </SidebarContext.Provider>
  );
};

function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export { useSidebar };
